import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { AnalyticsEvent } from '../../core/events';
import {
  call, uploadMaterial,
  type GetToken, type GroupSummary, type MaterialSummary, type UploadInput,
} from '../api';
import { groupPath, groupsPath } from '../routes';
import { useUploads } from '../uploads';
import MaterialUpload from './MaterialUpload';

/**
 * Adding a material, at an address of its own: `/g/:groupId/new`.
 *
 * It used to be a form sitting above the list in the group room, which made "share something"
 * the loudest thing on a screen whose subject is what the group has *already* shared. It is a
 * destination now, for the same reason a material is: a teacher goes there, the back button
 * brings them out, and a cold load of the address works — which is what makes "add a material"
 * a link a colleague can be sent rather than a control buried on another screen.
 *
 * The group is fetched here rather than handed in, because a cold load has nothing in hand.
 * That fetch is also the access check: the Worker re-decides membership on every request (R5),
 * and a 404 before the form ever rendered is "we could not find that group" while a 404 after
 * it did is "your access ended while you were looking at it" (AE4) — the same distinction the
 * group room and the material page draw, drawn the same way, from the same fact about the
 * session rather than about the id.
 *
 * **Where a success lands: on the new material's own page.** The teacher shared it to be read,
 * and that page is the one with the file on screen and the comment box beside it — so it both
 * confirms what the group will actually see (including whether the PDF renders at all) and
 * puts them where the first comment gets written. The navigation `replace`s this entry, so the
 * back button goes to the room rather than back into a spent form.
 *
 * **Several files go to the upload panel instead.** One file keeps this form — its title and
 * note describe that file. Pick more than one and they are handed to the queue whole, and the
 * teacher lands back in the room, where the panel shows each file's progress and the list
 * fills as they finish.
 */

export interface MaterialUploadPageProps {
  groupId: string;
  getToken: GetToken;
  track: (event: AnalyticsEvent) => void;
  /** Where a successful share lands. Given the material the Worker wrote. */
  onShared: (material: MaterialSummary) => void;
  /** Out of here without sharing: back to the room. */
  onCancel: () => void;
  /** Several files were handed to the upload panel: back to the room to watch them land. */
  onQueued?: () => void;
}

type Stage = 'loading' | 'ready' | 'missing' | 'failed' | 'lost';

export default function MaterialUploadPage({
  groupId, getToken, track, onShared, onCancel, onQueued,
}: MaterialUploadPageProps) {
  const uploads = useUploads();
  const [stage, setStage] = useState<Stage>('loading');
  const [group, setGroup] = useState<GroupSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  /** Whether the form ever rendered. The whole of "not found" versus "access ended". */
  const settled = useRef(false);

  const lost = useCallback(() => {
    setStage(settled.current ? 'lost' : 'missing');
  }, []);

  useEffect(() => {
    let live = true;
    setStage('loading');

    void (async () => {
      const result = await call<{ group: GroupSummary }>(
        getToken,
        `/api/groups/${encodeURIComponent(groupId)}`,
      );
      if (!live) return;
      if (!result.ok) {
        if (result.status === 404) { lost(); return; }
        setStage('failed');
        return;
      }
      setGroup(result.data.group);
      settled.current = true;
      setStage('ready');
    })();

    return () => { live = false; };
  }, [getToken, groupId, lost, attempt]);

  /**
   * Resolves `true` only when the Worker took the file — which is the signal `MaterialUpload`
   * empties itself on. A refusal resolves `false` and the teacher's file and words stay put.
   */
  async function share(input: UploadInput): Promise<boolean> {
    setBusy(true);
    setRefusal(null);
    const result = await uploadMaterial(getToken, groupId, input, track);
    setBusy(false);

    if (!result.ok) {
      if (result.status === 404) { lost(); return false; }
      setRefusal(result.reason ?? (result.status === 0 ? 'network' : result.error));
      return false;
    }

    onShared(result.data.material);
    return true;
  }

  if (stage === 'lost') {
    return (
      <section className="panel" aria-label="Add a material">
        <h2>You no longer have access to this group.</h2>
        <p>
          Your membership ended while this page was open. Anything you shared stays with the
          group.
        </p>
        <Link className="button-link" to={groupsPath()}>Back to your groups</Link>
      </section>
    );
  }

  if (stage === 'missing') {
    // The same one answer a stranger, a removed member and a deleted group all get (R5, AE1).
    return (
      <section className="panel" aria-label="Add a material">
        <h2>We could not find that group.</h2>
        <p>It may have been removed, or the link may be for a group you are not in.</p>
        <Link className="button-link" to={groupsPath()}>Back to your groups</Link>
      </section>
    );
  }

  if (stage === 'failed') {
    return (
      <section className="panel" aria-label="Add a material" role="alert">
        <h2>We could not open this group.</h2>
        <p>Try again in a moment.</p>
        <div className="row-actions">
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          <Link className="link" to={groupPath(groupId)}>Back to the group</Link>
        </div>
      </section>
    );
  }

  if (stage === 'loading' || group === null) {
    return (
      <section className="panel" aria-label="Add a material">
        <p className="hint" role="status">Opening this group…</p>
      </section>
    );
  }

  return (
    <section aria-label="Add a material">
      <div className="toolbar">
        <div className="toolbar-title">
          <h2>Add a material</h2>
          {/* Which group this lands in, said before the file is chosen rather than after. */}
          <p className="meta">
            Shared with <Link className="link" to={groupPath(groupId)}>{group.name}</Link>
          </p>
        </div>
        <div className="row-actions">
          <Link className="button-link" to={groupPath(groupId)}>Back to the group</Link>
        </div>
      </div>

      <MaterialUpload
        busy={busy}
        refusal={refusal}
        onUpload={share}
        onRejected={(reason) => setRefusal(reason)}
        onCancel={onCancel}
        {...(uploads ? {
          onMany: (files: File[]) => { uploads.add(groupId, files); onQueued?.(); },
        } : {})}
      />
    </section>
  );
}
